import type { Rect, V2 } from '../../core/math';
import type { BuildingStyle, District, PoiKind } from '../types';

/**
 * LOW BOROUGH, KESSMOUTH — authored district data.
 *
 * An original fictional port district: soot-black terraces, a Victorian railway viaduct, gas holders,
 * a canal ("the Gullet") with three bridges, a dock basin with cranes, a high street and a small
 * square with a church. ~600 x 600 m, north = -Z. North of Kessmouth Road, Kiln Common (600 x 180 m,
 * added 2026-10): a railway goods yard against the viaduct, a market street, terraces, the common and
 * a council estate going up — see `KILN_*` below and docs/ARCHITECTURE.md "World".
 *
 * Everything here is hand-authored; `build.ts` turns it into a full CityLayout (lots, colliders,
 * parking, ped loops, signals) deterministically.
 */

export const BOUNDS: Rect = { minX: -300, minZ: -480, maxX: 300, maxZ: 300 };

/**
 * The original Low Borough rectangle. Everything inside it is generated exactly as before Kiln Common
 * was added (same lots, same random stream), so the doors the POIs and premises stand at keep their
 * buildings; `build.ts` generates the new streets afterwards from their own random stream.
 */
export const CORE_BOUNDS: Rect = { minX: -300, minZ: -300, maxX: 300, maxZ: 300 };

export type StreetKind = 'arterial' | 'high' | 'street' | 'narrow' | 'alley' | 'bridge' | 'quay';

export interface StreetKindSpec {
  laneWidth: number;
  /** Width of the kerbside parking strip on each side (0 = none). */
  parking: number;
  sidewalk: number;
  speed: number;
  /** Kerb corner radius at junctions. */
  kerbRadius: number;
}

/**
 * Road classes. Widened 2026-10 for driving (docs/WORLD.md "Road widths"): the carriageways took the room from the
 * pavements, so each kind's building line (`laneWidth * lanes / 2 + parking + sidewalk` from the centre line) is
 * exactly where it was — every lot, landmark, door and POI stands where it stood. `ROAD_RESERVE` pins that.
 * Alleys are left narrow on purpose: they're the escape routes (walled, 4.8 m, police slow to a crawl in them).
 */
export const STREET_KINDS: Record<StreetKind, StreetKindSpec> = {
  arterial: { laneWidth: 4.6, parking: 2.0, sidewalk: 2.2, speed: 13.4, kerbRadius: 6 },
  high: { laneWidth: 4.6, parking: 2.4, sidewalk: 3.4, speed: 11.2, kerbRadius: 6 },
  street: { laneWidth: 3.8, parking: 2.0, sidewalk: 1.7, speed: 11.2, kerbRadius: 4.5 },
  narrow: { laneWidth: 3.9, parking: 2.0, sidewalk: 1.5, speed: 8.9, kerbRadius: 3 },
  alley: { laneWidth: 2.4, parking: 0, sidewalk: 0, speed: 6.7, kerbRadius: 1.2 },
  bridge: { laneWidth: 3.6, parking: 0, sidewalk: 1.5, speed: 11.2, kerbRadius: 3 },
  quay: { laneWidth: 4.0, parking: 0, sidewalk: 1.3, speed: 11.2, kerbRadius: 4 },
};

/**
 * Centre line to building line (m) per kind (two-way; a one-way `narrow` has one lane). Unchanged since the
 * streets were first laid out: the generator places every lot against it, so it must not move (layout.test.ts).
 */
export const ROAD_RESERVE: Record<StreetKind, number> = {
  arterial: 8.8,
  high: 10.4,
  street: 7.5,
  narrow: 5.45,
  alley: 2.4,
  bridge: 5.1,
  quay: 5.3,
};

/** Main roads (arterials, high streets, the quay): most traffic, and where the police run you down. */
export const MAIN_KINDS: ReadonlySet<StreetKind> = new Set(['arterial', 'high', 'quay']);

/** Node keys -> positions. Insertion order defines node ids (w_n = 0, cl_n = 1, ...). */
export const NODES: [string, number, number][] = [
  // Kessmouth Road (north arterial)
  ['w_n', -270, -250],
  ['cl_n', -200, -250],
  ['ar_n', -133, -250],
  ['gw_n', -60, -250],
  ['pa_n', 0, -250],
  ['bl_n', 40, -250],
  ['tr_n', 100, -250],
  ['t1_n', 130, -250],
  ['ps_n', 160, -250],
  ['t2_n', 190, -250],
  ['hl_n', 220, -250],
  ['e_n', 270, -250],
  // Bell Street / Furnace Street
  ['w_b', -270, -155],
  ['cl_b', -200, -155],
  ['gw_b', -60, -155],
  ['ga_b', -28, -155],
  ['pa_b', 0, -155],
  ['bl_b', 40, -155],
  ['tr_b', 100, -155],
  ['t1_b', 130, -155],
  ['ps_b', 160, -155],
  ['t2_b', 190, -155],
  ['hl_b', 220, -155],
  ['e_b', 270, -155],
  // Coalgate
  ['w_c', -270, -60],
  ['cl_c', -200, -60],
  ['ar_c', -133, -60],
  ['gw_c', -60, -60],
  ['ga_c', -28, -60],
  ['pa_c', 0, -60],
  ['bl_c', 40, -60],
  ['tr_c', 100, -60],
  ['t1_c', 130, -60],
  ['ps_c', 160, -60],
  ['t2_c', 190, -60],
  ['hl_c', 220, -60],
  ['e_c', 270, -60],
  // Mercy Square
  ['sq_nw', 100, -10],
  ['sq_n', 160, -10],
  ['sq_ne', 200, -10],
  ['sq_e', 200, 40],
  ['e_ch', 270, 40],
  ['sq_se', 200, 70],
  ['sq_sw', 100, 70],
  // Market Row
  ['cl_m', -200, 40],
  ['as_m', -60, 40],
  ['pa_m', 0, 40],
  // Wharf Road (north bank of the Gullet)
  ['w_w', -270, 130],
  ['cl_w', -200, 130],
  ['as_w', -60, 130],
  ['pa_w', 0, 130],
  ['tr_w', 100, 130],
  ['lk_w', 200, 130],
  ['e_w', 270, 130],
  // Dock Road (south bank)
  ['ws_d', -270, 195],
  ['cl_d', -200, 195],
  ['pa_d', 0, 195],
  ['qs_d', 85, 195],
  ['lk_d', 200, 195],
  // Bonded Alley
  ['sa_bo', 0, 240],
  ['qs_bo', 85, 240],
  // Ropewalk
  ['ws_r', -270, 280],
  ['cl_r', -200, 280],
  ['pa_r', 0, 280],
  ['qs_r', 85, 280],
  // --- Kiln Common (north of Kessmouth Road) ---
  // Kiln Road (north arterial)
  ['k_w', -270, -440],
  ['k_cd', -60, -440],
  ['k_ks', 0, -440],
  ['k_es', 100, -440],
  ['k_hl', 220, -440],
  // Lamb Walk
  ['lw_cd', -60, -345],
  ['lw_ks', 0, -345],
  ['lw_es', 100, -345],
  ['lw_hl', 220, -345],
  // Arch Row runs on north along the viaduct to Kiln Road
  ['k_ar', -133, -440],
];

export type FrontageRule =
  | { type: 'none' }
  | { type: 'yardwall' }
  | {
      type: 'lots';
      styles: BuildingStyle[];
      weights?: number[];
      width?: [number, number];
      depth?: [number, number];
      height?: [number, number];
      garden?: number;
    };

export interface StreetDef {
  name: string;
  kind: StreetKind;
  nodes: string[];
  /** One-way in node order. */
  oneWay?: boolean;
  /** Granite setts instead of asphalt (render only). */
  setts?: boolean;
  /** Frontage overrides, relative to node order (left = left of travel from first to last node). */
  left?: FrontageRule;
  right?: FrontageRule;
}

const NONE: FrontageRule = { type: 'none' };
const LOCKUPS: FrontageRule = { type: 'lots', styles: ['lockup'], width: [14, 24], depth: [6, 6.5], height: [2.8, 3] };
/** West side of Coal Drop Lane, backing onto the viaduct: coal merchants' sheds and lockups. */
const COAL_DROPS: FrontageRule = { type: 'lots', styles: ['shed', 'lockup', 'warehouse'], weights: [3, 2, 2], width: [12, 24], depth: [9, 16], height: [4, 9] };

export const STREETS: StreetDef[] = [
  { name: 'Kessmouth Road', kind: 'arterial', nodes: ['w_n', 'cl_n', 'ar_n', 'gw_n', 'pa_n', 'bl_n', 'tr_n', 't1_n', 'ps_n', 't2_n', 'hl_n', 'e_n'] },
  { name: 'Slag Road', kind: 'street', nodes: ['w_n', 'w_b', 'w_c', 'w_w'] },
  { name: 'Furnace Street', kind: 'street', nodes: ['w_b', 'cl_b'], setts: true },
  { name: 'Cinder Lane', kind: 'street', nodes: ['cl_n', 'cl_b', 'cl_c', 'cl_m', 'cl_w'] },
  { name: 'Cinder Bridge', kind: 'bridge', nodes: ['cl_w', 'cl_d'], left: NONE, right: NONE },
  { name: 'Cinder Lane', kind: 'street', nodes: ['cl_d', 'cl_r'] },
  // Service alley running the length of the viaduct's west face: the arches open onto it.
  { name: 'Arch Row', kind: 'alley', nodes: ['ar_n', 'ar_c'], setts: true, left: NONE, right: LOCKUPS },
  { name: 'Gasworks Lane', kind: 'street', nodes: ['gw_n', 'gw_b', 'gw_c'] },
  { name: 'Ashby Street', kind: 'street', nodes: ['gw_c', 'as_m', 'as_w'] },
  // Cut-through under the viaduct: a single arch, barely two cars wide.
  { name: 'Arch Lane', kind: 'alley', nodes: ['cl_m', 'as_m'], setts: true, left: { type: 'yardwall' }, right: { type: 'yardwall' } },
  { name: 'Market Row', kind: 'street', nodes: ['as_m', 'pa_m'], setts: true },
  { name: 'Gin Alley', kind: 'alley', nodes: ['ga_b', 'ga_c'], setts: true },
  { name: 'Bell Street', kind: 'street', nodes: ['gw_b', 'ga_b', 'pa_b', 'bl_b', 'tr_b', 't1_b', 'ps_b', 't2_b', 'hl_b', 'e_b'] },
  { name: 'The Parade', kind: 'high', nodes: ['pa_n', 'pa_b', 'pa_c', 'pa_m', 'pa_w'] },
  { name: 'Gullet Bridge', kind: 'bridge', nodes: ['pa_w', 'pa_d'], left: NONE, right: NONE },
  { name: 'Salt Street', kind: 'street', nodes: ['pa_d', 'sa_bo', 'pa_r'] },
  { name: 'Shambles Lane', kind: 'alley', nodes: ['bl_n', 'bl_b', 'bl_c'], setts: true },
  { name: 'Coalgate', kind: 'arterial', nodes: ['w_c', 'cl_c', 'ar_c', 'gw_c', 'ga_c', 'pa_c', 'bl_c', 'tr_c', 't1_c', 'ps_c', 't2_c', 'hl_c', 'e_c'] },
  { name: "Tanner's Row", kind: 'narrow', nodes: ['tr_n', 'tr_b', 'tr_c'], oneWay: true, setts: true },
  { name: 'Tanyard Ginnel', kind: 'alley', nodes: ['t1_n', 't1_b', 't1_c'], setts: true },
  { name: 'Pilcher Street', kind: 'narrow', nodes: ['ps_c', 'ps_b', 'ps_n'], oneWay: true },
  { name: 'Pilcher Ginnel', kind: 'alley', nodes: ['t2_n', 't2_b'], setts: true },
  { name: 'Pilcher Ginnel', kind: 'alley', nodes: ['t2_b', 't2_c'], setts: true, left: LOCKUPS },
  { name: 'Hob Lane', kind: 'street', nodes: ['hl_n', 'hl_b', 'hl_c'] },
  { name: 'Eastgate', kind: 'street', nodes: ['e_n', 'e_b', 'e_c', 'e_ch', 'e_w'] },
  { name: 'Mercy Street', kind: 'street', nodes: ['tr_c', 'sq_nw', 'sq_sw', 'tr_w'] },
  { name: 'Pilcher Street', kind: 'street', nodes: ['ps_c', 'sq_n'] },
  { name: 'Mercy Square', kind: 'street', nodes: ['sq_nw', 'sq_n', 'sq_ne', 'sq_e', 'sq_se', 'sq_sw'], setts: true },
  { name: 'Chapel Row', kind: 'street', nodes: ['sq_e', 'e_ch'] },
  { name: "Cutler's Slant", kind: 'street', nodes: ['pa_m', 'sq_sw'] },
  { name: 'Lock Lane', kind: 'street', nodes: ['sq_se', 'lk_w'] },
  { name: 'Lock Bridge', kind: 'bridge', nodes: ['lk_w', 'lk_d'], left: NONE, right: NONE },
  { name: 'Wharf Road', kind: 'arterial', nodes: ['w_w', 'cl_w', 'as_w', 'pa_w', 'tr_w', 'lk_w', 'e_w'] },
  { name: 'Dock Road', kind: 'arterial', nodes: ['ws_d', 'cl_d', 'pa_d', 'qs_d', 'lk_d'] },
  { name: 'Tarry Lane', kind: 'street', nodes: ['ws_d', 'ws_r'] },
  { name: 'Ropewalk', kind: 'street', nodes: ['ws_r', 'cl_r', 'pa_r', 'qs_r'], setts: true },
  { name: 'Quay Street', kind: 'quay', nodes: ['qs_d', 'qs_bo', 'qs_r'] },
  { name: 'Bonded Alley', kind: 'alley', nodes: ['sa_bo', 'qs_bo'], setts: true },
  // --- Kiln Common. Appended, so every older edge keeps its id. ---
  { name: 'Brickfield Lane', kind: 'street', nodes: ['w_n', 'k_w'] },
  // Arch Row's northern half: between the viaduct's arches and the goods yard fence.
  { name: 'Arch Row', kind: 'alley', nodes: ['ar_n', 'k_ar'], setts: true, left: NONE, right: NONE },
  { name: 'Coal Drop Lane', kind: 'street', nodes: ['gw_n', 'lw_cd', 'k_cd'], setts: true, left: COAL_DROPS },
  { name: 'Kiln Street', kind: 'high', nodes: ['pa_n', 'lw_ks', 'k_ks'] },
  { name: 'Hob Lane', kind: 'street', nodes: ['hl_n', 'lw_hl'] },
  // The common lies west of the top of Hob Lane, north of Lamb Walk's east end and south of Kiln Road's.
  { name: 'Hob Lane', kind: 'street', nodes: ['lw_hl', 'k_hl'], left: NONE },
  { name: 'Lamb Walk', kind: 'street', nodes: ['lw_cd', 'lw_ks', 'lw_es'], setts: true },
  { name: 'Lamb Walk', kind: 'street', nodes: ['lw_es', 'lw_hl'], setts: true, left: NONE },
  { name: 'Estate Road', kind: 'street', nodes: ['lw_es', 'k_es'], right: NONE },
  { name: 'Kiln Road', kind: 'arterial', nodes: ['k_w', 'k_ar', 'k_cd', 'k_ks', 'k_es'] },
  { name: 'Kiln Road', kind: 'arterial', nodes: ['k_es', 'k_hl'], right: NONE },
];

/** Streets with bus stops (world/view/streetLife.ts draws them, peds queue at them). */
export const BUS_STREETS: ReadonlySet<string> = new Set(['The Parade', 'Kessmouth Road', 'Coalgate', 'Wharf Road', 'Dock Road', 'Bell Street', 'Eastgate', 'Kiln Road', 'Kiln Street']);

/** Junctions with traffic lights. */
export const SIGNALLED = ['pa_n', 'pa_b', 'pa_c', 'pa_m', 'pa_w', 'pa_d', 'cl_c', 'cl_w', 'gw_c', 'tr_c', 'e_c', 'lk_w', 'cl_d', 'qs_d', 'lw_ks', 'k_ks'];

export const DISTRICTS: District[] = [
  {
    id: 'cinder-yards',
    name: 'Cinder Yards',
    bounds: { minX: -300, minZ: -300, maxX: -75, maxZ: 148 },
    character: ['industrial', 'railway', 'lockups', 'scrap'],
    pedDensity: 0.25,
    trafficDensity: 0.45,
    policePresence: 0.2,
  },
  {
    id: 'the-parade',
    name: 'The Parade',
    bounds: { minX: -75, minZ: -300, maxX: 60, maxZ: 148 },
    character: ['high-street', 'pubs', 'shops', 'busy', 'nightlife'],
    pedDensity: 0.95,
    trafficDensity: 0.8,
    policePresence: 0.65,
  },
  {
    id: 'tanners-row',
    name: "Tanner's Row",
    bounds: { minX: 60, minZ: -300, maxX: 300, maxZ: -55 },
    character: ['terraced', 'residential', 'alleys', 'lockups'],
    pedDensity: 0.55,
    trafficDensity: 0.4,
    policePresence: 0.3,
  },
  {
    id: 'mercy-square',
    name: 'Mercy Square',
    bounds: { minX: 60, minZ: -55, maxX: 300, maxZ: 148 },
    character: ['square', 'church', 'flats', 'residential', 'cafes'],
    pedDensity: 0.7,
    trafficDensity: 0.55,
    policePresence: 0.4,
  },
  {
    id: 'gullet-basin',
    name: 'Gullet Basin',
    bounds: { minX: -300, minZ: 148, maxX: 300, maxZ: 300 },
    character: ['docks', 'canal', 'warehouses', 'industrial'],
    pedDensity: 0.3,
    trafficDensity: 0.5,
    policePresence: 0.25,
  },
  {
    id: 'kiln-common',
    name: 'Kiln Common',
    bounds: { minX: -300, minZ: -480, maxX: 300, maxZ: -300 },
    character: ['market', 'terraced', 'residential', 'goods-yard', 'common', 'estate'],
    pedDensity: 0.6,
    trafficDensity: 0.5,
    policePresence: 0.3,
  },
];

export function districtAt(p: V2): string {
  for (const d of DISTRICTS) {
    const b = d.bounds;
    if (p.x >= b.minX && p.x < b.maxX && p.z >= b.minZ && p.z < b.maxZ) return d.id;
  }
  // Outside the authored rects (beyond the boundary): nearest by clamping.
  const c = { x: Math.max(BOUNDS.minX + 1, Math.min(BOUNDS.maxX - 1, p.x)), z: Math.max(BOUNDS.minZ + 1, Math.min(BOUNDS.maxZ - 1, p.z)) };
  for (const d of DISTRICTS) {
    const b = d.bounds;
    if (c.x >= b.minX && c.x < b.maxX && c.z >= b.minZ && c.z < b.maxZ) return d.id;
  }
  return 'the-parade';
}

/** Default frontage per district and street kind. */
export function defaultFrontage(district: string, kind: StreetKind): FrontageRule {
  if (kind === 'alley') return { type: 'yardwall' };
  if (kind === 'bridge') return NONE;
  switch (district) {
    case 'the-parade':
      if (kind === 'high' || kind === 'arterial')
        return { type: 'lots', styles: ['shopfront', 'shopfront', 'cafe', 'office', 'shopfront'], width: [7, 11], depth: [11, 14], height: [9, 13] };
      return { type: 'lots', styles: ['office', 'flats', 'shopfront', 'terrace', 'warehouse'], weights: [3, 2, 2, 2, 1], width: [9, 18], depth: [10, 15], height: [8, 14] };
    case 'tanners-row':
      if (kind === 'arterial')
        return { type: 'lots', styles: ['terrace', 'shopfront', 'terrace', 'flats'], weights: [4, 2, 2, 1], width: [10, 26], depth: [9, 11], height: [7, 10] };
      return { type: 'lots', styles: ['terrace'], width: [20, 42], depth: [8.5, 9.5], height: [6.5, 7], garden: 0.9 };
    case 'mercy-square':
      return { type: 'lots', styles: ['terrace', 'flats', 'shopfront', 'cafe', 'office'], weights: [4, 3, 2, 1, 1], width: [10, 28], depth: [9, 13], height: [7, 15] };
    case 'cinder-yards':
      return { type: 'lots', styles: ['warehouse', 'factory', 'shed', 'lockup', 'garage'], weights: [4, 3, 3, 1, 1], width: [14, 34], depth: [12, 26], height: [5, 13] };
    case 'gullet-basin':
      return { type: 'lots', styles: ['warehouse', 'warehouse', 'shed', 'office'], weights: [5, 3, 2, 1], width: [18, 40], depth: [14, 28], height: [10, 21] };
    case 'kiln-common':
      // Kiln Street is the market: shops shoulder to shoulder; Kiln Road a mixed main road; the rest terraces.
      if (kind === 'high') return { type: 'lots', styles: ['shopfront', 'shopfront', 'cafe', 'shopfront', 'office'], width: [7, 11], depth: [11, 14], height: [9, 12] };
      if (kind === 'arterial')
        return { type: 'lots', styles: ['terrace', 'shopfront', 'warehouse', 'flats'], weights: [3, 2, 2, 1], width: [10, 24], depth: [9, 14], height: [7, 12] };
      return { type: 'lots', styles: ['terrace', 'terrace', 'shopfront'], weights: [4, 4, 1], width: [16, 36], depth: [8.5, 10], height: [6.5, 8] };
    default:
      return { type: 'lots', styles: ['terrace'], width: [12, 24], depth: [9, 10], height: [7, 8] };
  }
}

/** Water bodies (metres). The canal runs the full width; the basin opens to the estuary. */
export const CANAL: Rect = { minX: -780, minZ: 150, maxX: 780, maxZ: 175 };
export const BASIN: Rect = { minX: 100, minZ: 218, maxX: 780, maxZ: 780 };
export const WATER: Rect[] = [CANAL, BASIN];

/** Railway viaduct centreline x and deck width. Runs the full north-south length. */
export const VIADUCT = { x: -120, halfWidth: 5, deckHeight: 9.5 };

/** Kiln Road Goods Depot: the fenced yard between Brickfield Lane, Kiln Road and the viaduct. */
export const KILN_GOODS_YARD: Rect = { minX: -262.5, minZ: -431, maxX: -137, maxZ: -305 };
/** Kiln Common: the railed green between Estate Road, Lamb Walk, Hob Lane and Kiln Road. */
export const KILN_COMMON: Rect = { minX: 107.8, minZ: -431, maxX: 212.2, maxZ: -352.8 };
/** Site hoarding round Block B of the Kiln Common Estate (going up in 1970). */
export const KILN_ESTATE_SITE: Rect = { minX: 262, minZ: -430, maxX: 296, maxZ: -370 };

export interface ExclusionZone {
  rect: Rect;
  reason: string;
}

/** Areas the frontage filler must leave alone (landmarks, water, yards). */
export const ZONES: ExclusionZone[] = [
  { rect: { minX: -400, minZ: 139, maxX: 400, maxZ: 186 }, reason: 'canal + quays' },
  { rect: { minX: 92, minZ: 204, maxX: 400, maxZ: 400 }, reason: 'basin' },
  { rect: { minX: 204, minZ: 176, maxX: 400, maxZ: 220 }, reason: 'dock apron' },
  { rect: { minX: -126.5, minZ: -600, maxX: -113.5, maxZ: 400 }, reason: 'viaduct' },
  { rect: { minX: -113, minZ: -236, maxX: -68, maxZ: -112 }, reason: 'gasworks' },
  { rect: { minX: -262.5, minZ: -6, maxX: -207.5, maxZ: 121.2 }, reason: 'scrap yard' },
  { rect: { minX: 107.5, minZ: -2.5, maxX: 192.5, maxZ: 62.5 }, reason: 'mercy square gardens' },
  { rect: { minX: 207.5, minZ: -51.2, maxX: 262.5, maxZ: 32.5 }, reason: "st brannoc's churchyard" },
  { rect: KILN_GOODS_YARD, reason: 'goods yard' },
  { rect: { minX: KILN_COMMON.minX - 0.5, minZ: KILN_COMMON.minZ - 0.5, maxX: KILN_COMMON.maxX + 0.5, maxZ: KILN_COMMON.maxZ + 0.5 }, reason: 'kiln common' },
  { rect: { minX: 227, minZ: -432, maxX: 300, maxZ: -366 }, reason: 'estate site' },
];

export interface LandmarkLot {
  key: string;
  style: BuildingStyle;
  center: V2;
  halfX: number;
  halfZ: number;
  angle: number;
  height: number;
  name?: string;
  tags?: string[];
}

const PI = Math.PI;

/** Hand-placed buildings (placed before the frontage filler). */
export const LANDMARKS: LandmarkLot[] = [
  { key: 'crooked-anchor', style: 'pub', center: { x: 19.5, z: -77.5 }, halfX: 9, halfZ: 8.5, angle: 0, height: 10, name: 'The Crooked Anchor', tags: ['corner', 'lit'] },
  { key: 'velvet-lamp', style: 'club', center: { x: -18, z: -112 }, halfX: 11, halfZ: 7.5, angle: PI / 2, height: 11, name: 'The Velvet Lamp', tags: ['lit', 'nightlife'] },
  { key: 'savings-bank', style: 'bank', center: { x: 19, z: 22 }, halfX: 9, halfZ: 8.5, angle: -PI / 2, height: 13, name: 'Kessmouth Savings Bank', tags: ['corner'] },
  { key: 'police-station', style: 'station', center: { x: -36, z: 109 }, halfX: 13, halfZ: 10, angle: 0, height: 11, name: 'Low Borough Police Station', tags: ['police'] },
  { key: 'st-brannocs', style: 'church', center: { x: 234.5, z: -12 }, halfX: 8, halfZ: 18, angle: -PI / 2, height: 15, name: "St. Brannoc's", tags: ['nave'] },
  { key: 'st-brannocs-tower', style: 'church', center: { x: 212.6, z: -12 }, halfX: 4.2, halfZ: 4.2, angle: -PI / 2, height: 36, name: "St. Brannoc's", tags: ['tower'] },
  { key: 'halloran-motors', style: 'garage', center: { x: -221, z: -29 }, halfX: 13, halfZ: 13, angle: PI / 2, height: 7, name: 'Halloran Motors', tags: ['forecourt'] },
  { key: 'bonded-3', style: 'warehouse', center: { x: 43.5, z: 220.8 }, halfX: 35, halfZ: 16, angle: PI, height: 21, name: 'Bonded Warehouse No. 3', tags: ['bonded', 'hoist'] },
  { key: 'wire-works', style: 'factory', center: { x: -167, z: -279 }, halfX: 27, halfZ: 18, angle: 0, height: 11, name: 'Kessmouth Wire & Cable Co.', tags: ['sawtooth', 'chimney'] },
  { key: 'brannoc-house', style: 'flats', center: { x: 150, z: 97 }, halfX: 30, halfZ: 7.5, angle: PI, height: 17, name: 'Brannoc House', tags: ['deck-access'] },
  { key: 'mercy-cafe', style: 'cafe', center: { x: 114, z: 82.5 }, halfX: 5.5, halfZ: 5, angle: -PI / 2, height: 7.5, name: "Rosa's Café", tags: ['lit'] },
  { key: 'gullet-arms', style: 'pub', center: { x: 225, z: 113 }, halfX: 9, halfZ: 7.5, angle: 0, height: 10, name: 'The Gullet Arms', tags: ['corner', 'lit'] },
  { key: 'three-cranes', style: 'pub', center: { x: -19, z: 211.5 }, halfX: 10, halfZ: 7.5, angle: PI, height: 9, name: 'The Three Cranes', tags: ['lit'] },
  { key: 'pike-respray', style: 'shed', center: { x: -228, z: 228 }, halfX: 14, halfZ: 11, angle: PI / 2, height: 7, name: "Pike's Respray", tags: ['respray'] },
  // --- Kiln Common ---
  { key: 'kiln-goods-shed', style: 'warehouse', center: { x: -190, z: -385 }, halfX: 42, halfZ: 11, angle: 0, height: 12, name: 'Kiln Road Goods Depot', tags: ['hoist'] },
  { key: 'kiln-goods-office', style: 'office', center: { x: -245, z: -418 }, halfX: 8, halfZ: 6, angle: PI, height: 7, name: 'Goods Office' },
  { key: 'kiln-coal-drops', style: 'shed', center: { x: -149, z: -342 }, halfX: 9, halfZ: 30, angle: 0, height: 8, name: 'Coal Drops' },
  { key: 'brickmakers', style: 'pub', center: { x: 19.5, z: -361.2 }, halfX: 9, halfZ: 8.5, angle: 0, height: 10, name: 'The Brickmakers Arms', tags: ['corner', 'lit'] },
  { key: 'kiln-motors', style: 'garage', center: { x: 55, z: -462.5 }, halfX: 13, halfZ: 13, angle: 0, height: 7, name: 'Kiln Road Motors', tags: ['forecourt'] },
  { key: 'coal-drop-lockups', style: 'lockup', center: { x: -71, z: -400 }, halfX: 12, halfZ: 3.2, angle: PI / 2, height: 3, name: 'Coal Drop Lockups' },
  { key: 'kiln-estate-a', style: 'flats', center: { x: 241, z: -398 }, halfX: 28, halfZ: 8, angle: -PI / 2, height: 21, name: 'Kiln Common Estate', tags: ['deck-access'] },
  { key: 'kiln-estate-b', style: 'flats', center: { x: 280, z: -401 }, halfX: 22, halfZ: 8, angle: -PI / 2, height: 8.5, name: 'Kiln Common Estate, Block B', tags: ['deck-access', 'construction'] },
];

export interface PoiDef {
  id: string;
  kind: PoiKind;
  name: string;
  pos: V2;
  heading: number;
  radius: number;
}

/** POIs. Positions are on the pavement / forecourt in front of the door. */
export const POIS: PoiDef[] = [
  { id: 'pub-crooked-anchor', kind: 'pub', name: 'The Crooked Anchor', pos: { x: 19.5, z: -67.2 }, heading: 0, radius: 3 },
  { id: 'club-velvet-lamp', kind: 'club', name: 'The Velvet Lamp', pos: { x: -8.2, z: -112 }, heading: PI / 2, radius: 3 },
  { id: 'bank-savings', kind: 'bank', name: 'Kessmouth Savings Bank', pos: { x: 8.2, z: 22 }, heading: -PI / 2, radius: 3 },
  { id: 'police-station', kind: 'policeStation', name: 'Low Borough Police Station', pos: { x: -36, z: 122.8 }, heading: 0, radius: 4 },
  { id: 'garage-halloran', kind: 'garage', name: 'Halloran Motors', pos: { x: -206, z: -29 }, heading: PI / 2, radius: 6 },
  { id: 'respray-pike', kind: 'resprayShop', name: "Pike's Respray", pos: { x: -210, z: 228 }, heading: PI / 2, radius: 6 },
  { id: 'lockup-arch-14', kind: 'lockup', name: 'Arch 14 Lockup', pos: { x: -129.5, z: -186 }, heading: -PI / 2, radius: 4 },
  { id: 'lockup-pilcher-7', kind: 'lockup', name: 'Lockup 7, Pilcher Ginnel', pos: { x: 191, z: -112 }, heading: PI / 2, radius: 4 },
  { id: 'safehouse', kind: 'safehouse', name: '9 Pilcher Street', pos: { x: 164.5, z: -206 }, heading: -PI / 2, radius: 2.5 },
  { id: 'docks-bonded-3', kind: 'missionTarget', name: 'Bonded Warehouse No. 3', pos: { x: 43.5, z: 202 }, heading: PI, radius: 6 },
  { id: 'docks-crane-quay', kind: 'missionTarget', name: 'Basin Crane Quay', pos: { x: 230, z: 205 }, heading: 0, radius: 8 },
  { id: 'scrapyard-gorringe', kind: 'hideout', name: "Gorringe's Scrap Metals", pos: { x: -262, z: 60 }, heading: PI / 2, radius: 8 },
  { id: 'gasworks-yard', kind: 'missionTarget', name: 'Gasworks Yard', pos: { x: -70, z: -175 }, heading: -PI / 2, radius: 6 },
  { id: 'mercy-square', kind: 'landmark', name: 'Mercy Square Gardens', pos: { x: 150, z: 30 }, heading: 0, radius: 10 },
  { id: 'st-brannocs', kind: 'landmark', name: "St. Brannoc's Church", pos: { x: 205, z: -12 }, heading: -PI / 2, radius: 4 },
  { id: 'phone-parade', kind: 'phonebox', name: 'Phone Box, The Parade', pos: { x: -8.6, z: -182 }, heading: PI / 2, radius: 2 },
  { id: 'phone-coalgate', kind: 'phonebox', name: 'Phone Box, Coalgate', pos: { x: 78, z: -52.4 }, heading: PI, radius: 2 },
  { id: 'phone-mercy', kind: 'phonebox', name: 'Phone Box, Mercy Square', pos: { x: 106.4, z: 30 }, heading: -PI / 2, radius: 2 },
  { id: 'phone-dock', kind: 'phonebox', name: 'Phone Box, Dock Road', pos: { x: -60, z: 202.4 }, heading: 0, radius: 2 },
  { id: 'phone-tanners', kind: 'phonebox', name: "Phone Box, Bell Street", pos: { x: 115, z: -148.6 }, heading: 0, radius: 2 },
  { id: 'mission-start-anchor', kind: 'missionStart', name: 'The Crooked Anchor (back room)', pos: { x: 38.6, z: -80 }, heading: PI / 2, radius: 2.5 },
  // Production premises (online, docs/PROPERTIES.md): doors of existing buildings, on the pavement / yard in
  // front. Markers only — no props, no colliders, not cover for the police.
  { id: 'warehouse-gullet-wharf', kind: 'missionTarget', name: 'Gullet Wharf Co. warehouse', pos: { x: -37.6, z: 274.7 }, heading: PI, radius: 6 },
  { id: 'printworks-moss-yard', kind: 'missionTarget', name: "Old printworks, Moss's Yard", pos: { x: 36.3, z: -210.6 }, heading: -PI / 2, radius: 4 },
  { id: 'smallholding-slagbank', kind: 'missionTarget', name: 'Slagbank smallholding', pos: { x: -211, z: -256.6 }, heading: PI, radius: 7 },
  // --- Kiln Common (appended: older code that takes "the first lockup / garage" still gets the same one) ---
  { id: 'pub-brickmakers', kind: 'pub', name: 'The Brickmakers Arms', pos: { x: 19.5, z: -351.1 }, heading: 0, radius: 3 },
  { id: 'phone-kiln-street', kind: 'phonebox', name: 'Phone Box, Kiln Street', pos: { x: -8.6, z: -318 }, heading: PI / 2, radius: 2 },
  { id: 'garage-kiln-motors', kind: 'garage', name: 'Kiln Road Motors', pos: { x: 55, z: -447.2 }, heading: 0, radius: 6 },
  { id: 'lockup-coal-drop', kind: 'lockup', name: 'Lockup 3, Coal Drop Lane', pos: { x: -66.3, z: -400 }, heading: PI / 2, radius: 4 },
  { id: 'shop-lamb-walk', kind: 'shop', name: 'Lamb Walk Market', pos: { x: 8.4, z: -322 }, heading: -PI / 2, radius: 3 },
  { id: 'kiln-common', kind: 'landmark', name: 'Kiln Common', pos: { x: 160, z: -386 }, heading: 0, radius: 10 },
  { id: 'kiln-goods-depot', kind: 'landmark', name: 'Kiln Road Goods Depot', pos: { x: -223, z: -424 }, heading: PI, radius: 6 },
];

/** Player start: outside The Crooked Anchor, corner of The Parade and Coalgate. */
export const PLAYER_SPAWN = { pos: { x: 8.2, z: -67.2 }, heading: PI };

/** Rect kept free of parked cars/props (the scaffold test car spawns here, driving east along Coalgate). */
export const SPAWN_CLEAR: Rect = { minX: -4, minZ: -70, maxX: 118, maxZ: -62 };

/** Zebra crossings: [street node a, node b, t along edge]. */
export const ZEBRAS: [string, string, number][] = [
  ['pa_n', 'pa_b', 0.55],
  ['pa_c', 'pa_m', 0.5],
  ['tr_w', 'lk_w', 0.5],
  ['gw_b', 'ga_b', 0.5],
  ['pa_n', 'lw_ks', 0.55],
];

/** Railway viaduct openings are derived from the street network; these are extra lockup arch numbers. */
export const ARCH_NAMES = ['Arch 9', 'Arch 11', 'Arch 14', 'Arch 17'];
